// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Shared types and helpers for the contract-negotiation report. Each tab
 * component pulls what it needs from here instead of duplicating logic or
 * threading props through the layout shell.
 */
import { type FC, type ReactNode } from 'react'
import { AlertTriangle, XCircle } from 'lucide-react'

export type NegotiationSourceType = 'contract_text' | 'tracked_insertion' | 'tracked_deletion'

export interface NegotiationFinding {
  knowledgeItem: string
  status: 'fallback' | 'reject'
  contractText: string
  sourceType: NegotiationSourceType
  summary: string | null
  sectionReference: string | null
  recommendation: string
  pageNumber: number | null
}

/** Evidence-block label per source_type. */
export const SOURCE_TYPE_LABEL: Record<string, string> = {
  contract_text: 'Evidence',
  tracked_insertion: 'Added',
  tracked_deletion: 'Removed',
}

/** Label color per source_type for the evidence block. */
export const SOURCE_TYPE_LABEL_CLASS: Record<string, string> = {
  contract_text: 'text-foreground',
  tracked_insertion: 'text-green-700',
  tracked_deletion: 'text-red-700',
}

/** Quoted-text color per source_type for the evidence block. */
export const SOURCE_TYPE_TEXT_CLASS: Record<string, string> = {
  contract_text: 'text-foreground bg-muted/50',
  tracked_insertion: 'text-green-900 bg-green-50',
  tracked_deletion: 'text-red-900 bg-red-50',
}

export interface NegotiationKbItem {
  id: string
  content: string
  /** Structured field values when the KB instance has an item schema. */
  fields?: Record<string, string> | null
}

export const STATUS_LABEL: Record<string, string> = {
  fallback: 'Fallback',
  reject: 'Reject',
}

export const STATUS_BADGE_CLASS: Record<string, string> = {
  fallback: 'bg-amber-100 text-amber-800 border-amber-200',
  reject: 'bg-red-100 text-red-800 border-red-200',
}

export function statusIcon(status: string, size = 'h-4 w-4'): ReactNode {
  switch (status) {
    case 'fallback':
      return <AlertTriangle className={`${size} text-amber-500 shrink-0`} aria-hidden="true" />
    case 'reject':
      return <XCircle className={`${size} text-red-500 shrink-0`} aria-hidden="true" />
    default:
      return <AlertTriangle className={`${size} text-amber-500 shrink-0`} aria-hidden="true" />
  }
}

export const PageLink: FC<{ page: number; onOpen?: (page: number) => void }> = ({ page, onOpen }) => {
  if (!onOpen) {
    return <span className="text-xs text-muted-foreground">p. {page}</span>
  }
  return (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(page) }}
      className="text-xs text-primary underline decoration-primary/40 hover:decoration-primary cursor-alias"
      title={`Open page ${page} with highlights`}
      aria-label={`Open annotated PDF for page ${page} in new tab`}
    >
      p. {page}
    </button>
  )
}

export function normalizeKbId(id: string): string {
  const n = Number(id)
  return Number.isFinite(n) ? String(n) : id
}

export function kbTitle(content: string): string {
  const newline = content.indexOf('\n')
  return newline === -1 ? content : content.slice(0, newline).trim()
}

export function worstStatus(findings: NegotiationFinding[]): string | null {
  if (!findings.length) return null
  if (findings.some(f => f.status === 'reject')) return 'reject'
  return 'fallback'
}

export const EmptyState: FC<{ message: string }> = ({ message }) => (
  <p className="text-sm text-muted-foreground p-6" role="status">{message}</p>
)
